@if (mode === 'link') {
  <div class="min-h-[60vh] flex items-center justify-center px-4 py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-md p-8 max-w-md w-full text-center space-y-4">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary mx-auto"></span>
      <h2 class="text-xl font-bold text-base-content">Redirecting to Refund Policy...</h2>
      <p class="text-xs text-base-content/70">
        You are being redirected to {{ academyName }}'s external Refund Policy.
      </p>
      @if (externalUrl) {
        <div class="pt-2">
          <a [href]="externalUrl" class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2">
            <span>Click here if not redirected</span>
            <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i>
          </a>
        </div>
      }
    </div>
  </div>
} @else if (mode === 'custom') {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-3xl mx-auto">
      <span
        class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70"
      >
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">
        Refund Policy
      </h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        Learn how {{ academyName }} manages course cancellations, guarantees, and refund requests.
      </p>
      @if (lastUpdated) {
        <p class="text-xs text-base-content/60 mt-3 flex items-center justify-center gap-1.5">
          <i class="fa-regular fa-calendar"></i>
          <span>Last Updated: {{ lastUpdated | date:'mediumDate' }}</span>
        </p>
      }
    </div>
  </div>

  <!-- Custom Page Body -->
  <div class="max-w-4xl mx-auto px-6 py-10 md:py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-2xl p-6 sm:p-10">
      <div class="ql-snow">
        <div class="ql-editor !p-0 max-w-none text-base-content leading-relaxed" [innerHTML]="customContent"></div>
      </div>
    </div>
  </div>
} @else {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-2xl mx-auto">
      <span
        class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70"
      >
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">
        Refund Policy
      </h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        At {{ academyName }}, we want to ensure your complete satisfaction with our
        educational services. This policy outlines our refund process for course
        subscriptions and enrollments.
      </p>
    </div>
  </div>

  <!-- Page Body -->
  <div
    class="flex flex-col md:flex-row gap-10 max-w-6xl mx-auto px-6 py-10 md:py-16 items-start"
  >
    <!-- Left Sidebar -->
    <aside class="w-full md:w-60 md:shrink-0 md:sticky md:top-20">
      <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-xl p-6">
        <h2 class="text-base font-bold text-base-content mb-2">At a Glance</h2>
        <p class="text-xs text-base-content/70 leading-relaxed mb-4">
          Details on eligibility, timelines, and step-by-step refund requests for {{ academyName }}.
        </p>

        <div class="flex flex-col gap-3 pb-4 border-b border-base-200 mb-4">
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i class="fa-regular fa-calendar text-base-content/50 mt-0.5 shrink-0"></i>
            <div>
              <span class="block font-semibold text-base-content">Last Updated</span>
              <span class="block text-base-content/70">{{ (lastUpdated | date:'mediumDate') || 'June 2024' }}</span>
            </div>
          </div>
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i
              class="fa-regular fa-circle-question text-base-content/50 mt-0.5 shrink-0"
            ></i>
            <div>
              <span class="block font-semibold text-base-content">Questions?</span>
              <a
                [href]="'mailto:' + contactEmail"
                class="block text-primary hover:underline break-all"
                >{{ contactEmail }}</a
              >
            </div>
          </div>
        </div>

        <!-- Jump to Section -->
        <div>
          <h3
            class="text-[10px] font-bold tracking-wider text-base-content/50 uppercase mb-2.5"
          >
            JUMP TO SECTION
          </h3>
          <nav class="flex flex-col gap-1">
            <a
              href="refund#section-1"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >1. Refund Eligibility</a
            >
            <a
              href="refund#section-2"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >2. Non-Refundable Items</a
            >
            <a
              href="refund#section-3"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >3. Refund Process</a
            >
            <a
              href="refund#contact"
              class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >Need Help?</a
            >
          </nav>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="flex-1 min-w-0">
      <p class="text-sm text-base-content/80 leading-relaxed mb-8">
        At {{ academyName }}, we want to ensure your complete satisfaction with our
        educational services. This policy outlines our refund process for course
        subscriptions.
      </p>

      <!-- Section 1 -->
      <section id="section-1" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">1.</span> Refund Eligibility
        </h2>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            New subscriptions within 14 days of purchase.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Live classes which are not started.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Account must be in good standing.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Original payment method must be active.
          </li>
        </ul>
      </section>

      <!-- Section 2 -->
      <section id="section-2" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">2.</span> Non-Refundable Items
        </h2>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            Subscription renewal payments.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Special promotional offers or discounted subscriptions.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Course materials already downloaded.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            Subscriptions active for more than 14 days.
          </li>
        </ul>
      </section>

      <!-- Section 3 -->
      <section id="section-3" class="mb-8 scroll-mt-20">
        <h2
          class="text-lg font-bold text-base-content mb-5 flex items-baseline gap-1"
        >
          <span class="text-primary font-bold">3.</span> Refund Process
        </h2>
        <div class="daisy-steps daisy-steps-vertical space-y-4">
          <div class="daisy-step daisy-step-primary text-left">
            <div class="pl-4">
              <h4 class="font-semibold text-sm text-base-content">
                Step 1: Submit Request
              </h4>
              <p class="text-xs text-base-content/70 leading-relaxed">
                Submit refund request through your account dashboard or contact
                support
              </p>
            </div>
          </div>
          <div class="daisy-step daisy-step-primary text-left">
            <div class="pl-4">
              <h4 class="font-semibold text-sm text-base-content">Step 2: Review</h4>
              <p class="text-xs text-base-content/70 leading-relaxed">
                Our team reviews your eligibility within 24-48 hours
              </p>
            </div>
          </div>
          <div class="daisy-step daisy-step-primary text-left">
            <div class="pl-4">
              <h4 class="font-semibold text-sm text-base-content">
                Step 3: Processing
              </h4>
              <p class="text-xs text-base-content/70 leading-relaxed">
                If approved, refund processed to original payment method. User
                should provide bank details if it is not the same as the payment
                method.
              </p>
            </div>
          </div>
          <div class="daisy-step daisy-step-primary text-left">
            <div class="pl-4">
              <h4 class="font-semibold text-sm text-base-content">
                Step 4: Completion
              </h4>
              <p class="text-xs text-base-content/70 leading-relaxed">
                Refund appears in your account within 5-7 business days
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Information / Need Help -->
      <section
        id="contact"
        class="scroll-mt-20 mt-10 pt-8 border-t border-base-200"
      >
        <h2 class="text-lg font-bold text-primary mb-1">Need Help?</h2>
        <p class="text-sm text-base-content/80 mb-5">
          For refund requests or questions about our policy, please contact us.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a
            [href]="'mailto:' + contactEmail"
            class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content transition duration-150 ease-in-out min-w-[200px] hover:border-primary hover:shadow-sm hover:text-primary"
          >
            <i class="fa-regular fa-envelope text-base-content/50"></i>
            <span>{{ contactEmail }}</span>
          </a>
          <div
            class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content min-w-[200px]"
          >
            <i class="fa-regular fa-clock text-base-content/50"></i>
            <span>Mon – Fri, 9 AM – 6 PM (Response: &lt;24h)</span>
          </div>
        </div>
      </section>
    </main>
  </div>
}
